import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, Shield } from "lucide-react";
import { useAuth } from "../AuthContext";
import { useIsMobile, merge } from "../hooks/useIsMobile";
import { usePublicAccess } from "../hooks/usePublicAccess";
import { COLORS, SANS, HEADER, NAV_ITEM } from "./dispute/theme";
import Logo from "./Logo";
import { clearDraft } from "../lib/draftCache";
import { useHasPublicDisputes } from "../hooks/useHasPublicDisputes";

// THE navbar. Every page renders this one — marketing, dispute, checkout, auth
// — so the bar never changes shape as you move around the site (it absorbed the
// old dispute/AppHeader, including its env badge and admin-session controls).
// Self-contained: reads auth + routing itself. Section links point at /home#…
// so they resolve from any page. The admin console keeps its own AdminHeader.
//
// Pair it with SiteFooter: header + footer are a set.

// Same key the AdminDisputes view stores its Basic-auth header under.
const ADMIN_STORAGE_KEY = "disputefai_admin_auth";

const navLinks = [
  // { href: "/home#how", label: "How it works" },
  // { href: "/home#panel", label: "The panel" },
  // { href: "/home#pricing", label: "Pricing" },
  // { href: "/home#faq", label: "FAQ" },
  // "Community disputes" lives in SiteFooter only.
];

// Appended only when the public index actually has something in it — see
// useHasPublicDisputes.
const disputesLink = { href: "/disputes", label: "Public Disputes", isRoute: true };

export default function SiteHeader() {
  const navigate = useNavigate();
  const { token, user, logout } = useAuth();
  const isMobile = useIsMobile();
  const publicAccess = usePublicAccess();
  const hasPublicDisputes = useHasPublicDisputes();
  const [open, setOpen] = useState(false);
  // When an admin session is active (sessionStorage entry from AdminDisputes),
  // the normal user controls are replaced by an ADMIN badge + sign-out.
  const [isAdmin, setIsAdmin] = useState(() =>
    typeof window !== "undefined" && !!sessionStorage.getItem(ADMIN_STORAGE_KEY)
  );

  const links = hasPublicDisputes ? [...navLinks, disputesLink] : navLinks;
  const env = process.env.REACT_APP_ENV || "local";

  useEffect(() => {
    if (!isMobile && open) setOpen(false);
  }, [isMobile, open]);

  // Pick up admin sign-in/out from another tab.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === ADMIN_STORAGE_KEY) {
        setIsAdmin(!!sessionStorage.getItem(ADMIN_STORAGE_KEY));
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const close = () => setOpen(false);
  // Starting a new dispute via "Settle it" should always be a blank form —
  // clear any draft left over from a previously abandoned submission (as
  // opposed to Checkout's own "Back" link, which intentionally restores it).
  const onSubmit = () => { clearDraft(); navigate("/submit-dispute"); };
  const onLogin = () => navigate(token ? "/submit-dispute" : "/login");
  const onDisputes = () => navigate("/all-disputes");
  const onLogout = () => { logout(); navigate("/home"); };
  const onAdminLogout = () => {
    sessionStorage.removeItem(ADMIN_STORAGE_KEY);
    setIsAdmin(false);
    navigate("/admin");
  };

  // While public access is closed, a logged-out visitor can't actually reach
  // /submit-dispute (it bounces to the gated /login). Point the CTA at the
  // beta request form instead — a real <a href> (not client-side navigate) so
  // the #apply scroll works the same way the other section links below do.
  const ctaLocked = !token && !publicAccess;

  return (
    <header style={s.nav}>
      <div style={merge(s.navInner, s.navInnerMobile, isMobile)}>
        <span onClick={close}>
          <Logo to="/home" style={{ color: "#000" }} iconColor="#1A5EC4" iconSize={28} />
        </span>

        {!isMobile && (
          <>
            {env !== "production" && <span style={s.envBadge}>{env}</span>}
            <div style={{ flex: 1 }} />
            <div style={s.navActions}>
              <nav style={s.navLinks}>
                {links.map((l) => (
                  l.isRoute
                    ? <Link key={l.href} to={l.href} style={s.navLink}>{l.label}</Link>
                    : <a key={l.href} href={l.href} style={s.navLink}>{l.label}</a>
                ))}
              </nav>
              {isAdmin && !token ? (
                // Admin session with no user logged in: the platform's own
                // controls replace the visitor ones.
                <>
                  <span style={s.adminBadge}>
                    <Shield size={11} strokeWidth={2.25} style={{ marginRight: 4 }} />
                    ADMIN
                  </span>
                  <button onClick={onAdminLogout} style={s.logoutBtn}>Sign out (admin)</button>
                </>
              ) : (
                <>
                  {!token && publicAccess && (
                    <button onClick={onLogin} style={s.navLoginBtn}>Log in</button>
                  )}
                  {ctaLocked && (
                    <button onClick={() => navigate("/login")} style={s.navLoginBtn}>Sign in</button>
                  )}
                  {ctaLocked
                    ? <a href="/home#apply" style={s.navCtaLink}>Request beta access →</a>
                    : <button onClick={onSubmit} style={s.navCta}>Settle it →</button>}
                  {token && (
                    <>
                      <button onClick={onDisputes} style={s.navLinkBtn}>My disputes</button>
                      <button onClick={onLogout} style={s.logoutBtn}>Log out</button>
                      <button onClick={onSubmit} style={s.username}>
                        {user?.username || user?.email}
                      </button>
                    </>
                  )}
                </>
              )}
            </div>
          </>
        )}

        {isMobile && (
          <button
            onClick={() => setOpen(!open)}
            aria-label={open ? "Close menu" : "Open menu"}
            style={s.burgerBtn}
          >
            {open ? <X size={22} strokeWidth={1.75} /> : <Menu size={22} strokeWidth={1.75} />}
          </button>
        )}
      </div>

      {isMobile && open && (
        <div style={s.mobileDrawer}>
          {links.map((l) => (
            l.isRoute
              ? <Link key={l.href} to={l.href} style={s.mobileDrawerLink} onClick={close}>{l.label}</Link>
              : <a key={l.href} href={l.href} style={s.mobileDrawerLink} onClick={close}>{l.label}</a>
          ))}
          <div style={s.mobileDrawerActions}>
            {env !== "production" && <span style={s.envBadge}>{env}</span>}
            {isAdmin && !token && (
              <>
                <span style={s.adminBadge}>
                  <Shield size={11} strokeWidth={2.25} style={{ marginRight: 4 }} />
                  ADMIN
                </span>
                <button onClick={() => { close(); onAdminLogout(); }} style={s.mobileDrawerLogin}>
                  Sign out (admin)
                </button>
              </>
            )}
            {!token && publicAccess && (
              <button onClick={() => { close(); onLogin(); }} style={s.mobileDrawerLogin}>
                Log in
              </button>
            )}
            {token && (
              <>
                <button onClick={() => { close(); onDisputes(); }} style={s.mobileDrawerLogin}>
                  My disputes
                </button>
                <button onClick={() => { close(); onSubmit(); }} style={s.mobileDrawerLogin}>
                  {user?.username || user?.email}
                </button>
                <button onClick={() => { close(); onLogout(); }} style={s.mobileDrawerLogin}>
                  Log out
                </button>
              </>
            )}
            {ctaLocked && (
              <button onClick={() => { close(); navigate("/login"); }} style={s.mobileDrawerLogin}>
                Sign in
              </button>
            )}
            {ctaLocked
              ? <a href="/home#apply" onClick={close} style={s.mobileDrawerCtaLink}>Request beta access →</a>
              : <button onClick={() => { close(); onSubmit(); }} style={s.mobileDrawerCta}>Settle it →</button>}
          </div>
        </div>
      )}
    </header>
  );
}

// Type for every item in the bar comes from NAV_ITEM in dispute/theme.js, so
// this header stays consistent with the admin console's AdminHeader — see the note
// there. Nothing below sets its own fontSize/fontWeight/textTransform.
const s = {
  // Like SiteFooter, the bar fixes its own font metrics rather than inheriting
  // them: pages disagree on font-size (14/15/browser default), line-height
  // (1.55 vs normal) and -webkit-font-smoothing (set on three page wrappers,
  // absent on the rest — it visibly changes stroke weight). Items still get
  // their family/size/weight from NAV_ITEM; these are the fallbacks that stop a
  // page from leaking into the chrome.
  nav: { position: "sticky", top: 0, zIndex: HEADER.zIndex, background: "#fff", backdropFilter: HEADER.backdropFilter, WebkitBackdropFilter: HEADER.backdropFilter, borderBottom: `1px solid ${COLORS.border}`, fontFamily: SANS, fontSize: 14, lineHeight: "normal", letterSpacing: "normal", textAlign: "left", WebkitFontSmoothing: "antialiased" },
  navInner: { display: "flex", alignItems: "center", padding: HEADER.paddingDesktop },
  navInnerMobile: { padding: HEADER.paddingMobile },
  navLinks: { display: "flex", gap: 32, alignItems: "center" },
  // Badges, not nav items — they keep their own small uppercase treatment
  // rather than NAV_ITEM (carried over from the old AppHeader).
  envBadge: { background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 4, padding: "2px 8px", fontSize: 11, color: COLORS.inkMuted, textTransform: "uppercase", letterSpacing: 1, marginLeft: 12 },
  adminBadge: { display: "inline-flex", alignItems: "center", background: COLORS.dark, color: "#fff", border: "none", borderRadius: 4, padding: "3px 8px", fontSize: 10, fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase", fontFamily: SANS },
  navLink: { ...NAV_ITEM, background: "transparent", border: "none", color: COLORS.ink, textDecoration: "none", transition: "color 0.15s", padding: "6px 10px" },
  navActions: { display: "flex", alignItems: "center", gap: 12 },
  navLoginBtn: { ...NAV_ITEM, background: "transparent", border: "none", color: COLORS.ink, cursor: "pointer", padding: "6px 10px" },
  navCta: { ...NAV_ITEM, background: "#1A5EC4", color: "#fff", padding: "6px 12px", borderRadius: 6, border: "none", cursor: "pointer", whiteSpace: "nowrap", transition: "background 0.18s" },
  navCtaLink: { ...NAV_ITEM, background: "#1A5EC4", color: "#fff", padding: "6px 12px", borderRadius: 6, border: "none", cursor: "pointer", whiteSpace: "nowrap", transition: "background 0.18s", textDecoration: "none", display: "inline-block" },
  navLinkBtn: { ...NAV_ITEM, background: "transparent", color: COLORS.ink, border: "1px solid rgba(26,94,196,0.2)", borderRadius: 6, padding: "6px 12px", cursor: "pointer" },
  logoutBtn: { ...NAV_ITEM, background: "transparent", color: COLORS.ink, border: "1px solid rgba(26,94,196,0.2)", borderRadius: 6, padding: "6px 12px", cursor: "pointer" },
  username: { ...NAV_ITEM, color: COLORS.ink, background: "transparent", border: "none", cursor: "pointer", padding: 0 },
  burgerBtn: { marginLeft: "auto", background: "transparent", border: "none", color: COLORS.ink, cursor: "pointer", padding: 6, display: "inline-flex", alignItems: "center", justifyContent: "center" },
  mobileDrawer: { borderTop: `1px solid ${COLORS.border}`, background: COLORS.bg, padding: "12px 20px 18px 20px", display: "flex", flexDirection: "column", gap: 4 },
  mobileDrawerLink: { ...NAV_ITEM, color: COLORS.ink, textDecoration: "none", padding: "12px 0", borderBottom: `1px solid ${COLORS.border}` },
  mobileDrawerActions: { display: "flex", flexDirection: "column", gap: 10, marginTop: 14 },
  mobileDrawerLogin: { ...NAV_ITEM, background: "transparent", border: "1px solid rgba(26,94,196,0.2)", color: COLORS.ink, padding: "12px 16px", borderRadius: 8, cursor: "pointer", width: "100%" },
  mobileDrawerCta: { ...NAV_ITEM, background: "#1A5EC4", color: "#fff", border: "none", padding: "12px 16px", borderRadius: 8, cursor: "pointer", width: "100%" },
  mobileDrawerCtaLink: { ...NAV_ITEM, background: "#1A5EC4", color: "#fff", border: "none", padding: "12px 16px", borderRadius: 8, cursor: "pointer", width: "100%", textDecoration: "none", display: "block", textAlign: "center", boxSizing: "border-box" },
};
